import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import moment from "moment";

import {
    fetchVendorBookings,
    vendorUpdateBooking,
} from "../redux/slices/bookingSlice";

/* ================================
   ONLY PENDING
================================ */
const TAB_STATUS_MAP = {
    Pending: ["pending", "submitted"],
};

const VendorAppointements = () => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");

    const { bookings, loading } = useSelector((state) => state.booking);
    const { user } = useSelector((state) => state.auth);
    const vendorId = user?.vendor?._id;

    /* ================= FETCH ================= */
    useEffect(() => {
        if (vendorId) {
            dispatch(fetchVendorBookings(vendorId));
        }
    }, [dispatch, vendorId]);

    /* ================= REFRESH ================= */
    const refreshData = useCallback(() => {
        dispatch(fetchVendorBookings(vendorId));
    }, [dispatch, vendorId]);

    /* ================= FILTER + SEARCH + SORT ================= */
    const filteredBookings = useMemo(() => {
        return bookings
            .filter((b) =>
                TAB_STATUS_MAP.Pending.includes(b.status)
            )
            .filter((b) => {
                if (!search) return true;

                const keyword = search.toLowerCase();
                return (
                    b.customerName?.toLowerCase().includes(keyword) ||
                    b.bookingId?.toLowerCase().includes(keyword)
                );
            })
            .reverse(); // 👈 New entry first
    }, [bookings, search]);

    /* ================= CALL ================= */
    const makeCall = (number) => {
        if (!number) return;
        window.location.href = `tel:${number}`;
    };

    /* ================= APPROVE ================= */
    const handleApprove = async (id) => {
        const confirm = await Swal.fire({
            title: "Approve booking?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#22C55E",
            confirmButtonText: "Approve",
        });

        if (!confirm.isConfirmed) return;

        try {
            await dispatch(
                vendorUpdateBooking({
                    bookingId: id,
                    action: "approve",
                })
            ).unwrap();

            Swal.fire({
                icon: "success",
                title: "Approved",
                text: "Booking approved successfully",
                timer: 1500,
                showConfirmButton: false,
            });

            refreshData();
        } catch (err) {
            Swal.fire("Error", err || "Approve failed", "error");
        }
    };

    /* ================= REJECT ================= */
    const handleReject = async (id) => {
        const confirm = await Swal.fire({
            title: "Reject booking?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#EF4444",
            confirmButtonText: "Reject",
        });

        if (!confirm.isConfirmed) return;

        try {
            await dispatch(
                vendorUpdateBooking({
                    bookingId: id,
                    action: "reject",
                })
            ).unwrap();

            Swal.fire({
                icon: "success",
                title: "Rejected",
                text: "Booking rejected successfully",
                timer: 1500,
                showConfirmButton: false,
            });

            refreshData();
        } catch (err) {
            Swal.fire("Error", err || "Reject failed", "error");
        }
    };

    /* ================= UI ================= */
    return (
        <div className="container py-4">
            <div className="card shadow-sm">
                <div className="card-header bg-dark d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-white">
                        My Vendor Appointements (Pending)
                    </h6>

                    {/* 🔍 SEARCH */}
                    <input
                        type="text"
                        className="form-control form-control-sm w-25"
                        placeholder="Search name or booking id"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" />
                        </div>
                    ) : filteredBookings.length === 0 ? (
                        <p className="text-center py-4 text-muted">
                            No Pending Bookings
                        </p>
                    ) : (
                        <table className="table table-sm table-hover mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Booking ID</th>
                                    <th>Customer</th>
                                    <th>Date & Time</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                    <th width="160">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.map((item) => (
                                    <tr key={item._id}>
                                        <td className="fw-semibold text-primary">
                                            #{item.bookingId}
                                        </td>

                                        <td>{item.customerName}</td>

                                        <td className="text-muted">
                                            {moment(item.date).format("DD-MM-YYYY")} <br />
                                            {moment(item.time, "HH:mm").format("hh:mm A")}
                                        </td>

                                        <td className="text-muted">
                                            {item.address}, {item.city}, {item.state}
                                        </td>

                                        <td>
                                            <span className="badge bg-warning text-dark">
                                                {item.status.toUpperCase()}
                                            </span>
                                        </td>

                                        <td>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-dark"
                                                    onClick={() => makeCall(item.phone)}
                                                >
                                                    Call
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => handleApprove(item._id)}
                                                >
                                                    Approve
                                                </button>

                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleReject(item._id)}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorAppointements;
